import React, { useEffect, useRef, useState } from "react";
import { FiCheck, FiCopy } from "react-icons/fi";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService.js";
import AppTooltip from "./AppTooltip";
import "./CopyButton.css";

// The Clipboard API needs a secure context (https / localhost); fall back to
// execCommand elsewhere.
export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
};

// Plain text of rich (HTML) content such as a comment: tags dropped, line
// breaks kept, entities decoded.
export const htmlToText = (html = "") => {
  const doc = new DOMParser().parseFromString(
    String(html).replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li)>/gi, "\n"),
    "text/html"
  );
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

// Copy icon (optionally with a "Copy" label) that shows a tick for 1.5s.
// `text` may be a string or a function, so costly text is built only on click.
const CopyButton = ({ text, label, title = "Copy", className = "" }) => {
  const [done, setDone] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const handleClick = async (e) => {
    e.stopPropagation();
    const value = typeof text === "function" ? text() : text;
    if (!value) return;
    if (await copyText(value)) {
      showSuccessToast("Copied");
      setDone(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setDone(false), 1500);
    } else {
      showErrorToast("Couldn't copy");
    }
  };

  return (
    <AppTooltip title={title}>
      <button
        type="button"
        className={`fd-copy-btn${label ? " fd-copy-btn--label" : ""}${done ? " fd-copy-btn--done" : ""} ${className}`}
        onClick={handleClick}
        aria-label={title}
      >
        {done ? <FiCheck size={12} /> : <FiCopy size={12} />}
        {label && <span>{done ? "Copied" : label}</span>}
      </button>
    </AppTooltip>
  );
};

export default CopyButton;